/**
 * Turning the credential form into a request body.
 *
 * Extracted from the component for the reason `verify-outcome.ts` and
 * `asset-form.ts` were: node cannot strip types from `.tsx`, so logic left
 * inline in a component is logic no test can reach — and the one decision this
 * form makes is the kind that has to be tested.
 *
 * ── 🔴 The rule this file exists for ─────────────────────────────────────
 *
 * `validateCredentialEdit` treats an absent `secret` and an empty one as
 * different things, and says why in its own comment:
 *
 *   > Distinguished from absent on purpose: absent means "leave it", and an
 *   > explicit empty string is almost always a form posting a field the user
 *   > never touched. Neither should blank a working password.
 *
 * So an edit that does not rotate the secret must **omit the key entirely**.
 * Sending `secret: ''` is refused by the server — correctly — and a form that
 * did it would be unable to rename a credential without also being told off
 * about a password nobody was trying to change.
 *
 * ── There is no reveal, and nowhere to put one ───────────────────────────
 *
 * `005_grants.sql` gives the API role INSERT and UPDATE on `secret_ciphertext`
 * and `secret_nonce` and deliberately NOT SELECT: it can write a secret and can
 * never read one back. `cosmon_runner` is the only role that may, because it is
 * the only one that talks to a device. Somebody who has lost a device password
 * REPLACES it here; they do not recover it, and no amount of screen work can
 * change that.
 */

/** Mirrors CREDENTIAL_KINDS in src/crypto/credential-edit.ts. */
export const CREDENTIAL_KINDS = ['userpass', 'token', 'ssh_key'] as const;
export type CredentialKind = (typeof CREDENTIAL_KINDS)[number];

export interface CredentialRecord {
  id: string;
  label: string;
  kind: string;
  username: string | null;
  key_version: number;
  created_at: string;
  rotated_at: string | null;
}

export interface CredentialDraft {
  label: string;
  kind: string;
  username: string;
  /** Empty means "not rotating". It is never sent as an empty string. */
  secret: string;
}

export const EMPTY_DRAFT: CredentialDraft = {
  label: '',
  kind: 'userpass',
  username: '',
  secret: '',
};

export const draftOf = (c: CredentialRecord): CredentialDraft => ({
  label: c.label,
  kind: c.kind,
  username: c.username ?? '',
  secret: '',
});

/** Raised before a request is made, so the form can say so without a round trip. */
export class CredentialFormError extends Error {}

/**
 * The body for a create or an update.
 *
 * 🔴 `creating` changes exactly one thing and it is the whole point: on create
 * the secret is required, and on update an untouched box means the key is left
 * out rather than sent empty.
 */
export function bodyFrom(
  draft: CredentialDraft,
  { creating }: { creating: boolean },
): Record<string, unknown> {
  const secret = draft.secret;

  if (creating && secret === '') {
    throw new CredentialFormError('a new credential needs a secret');
  }
  /*
   * ⚠️ The secret is NOT trimmed, and that is copied from the server's reasoning
   * rather than invented here: every other field is trimmed because a trailing
   * space in a label is a typo, but in a password it is a character. Trimming in
   * the browser would mean the device rejects a password the screen shows as
   * correct.
   */
  const body: Record<string, unknown> = {
    label: draft.label.trim(),
    kind: draft.kind,
    username: draft.kind === 'userpass' ? draft.username.trim() : null,
  };
  if (secret !== '') body['secret'] = secret;
  return body;
}

/**
 * Is this draft going to rotate the stored secret?
 *
 * Used to warn before it happens. Rotation is not reversible in any useful
 * sense — the old value cannot be read back to compare, so getting it wrong is
 * discovered by a fleet that stops authenticating.
 */
export const willRotate = (draft: CredentialDraft): boolean => draft.secret !== '';
